import React, { useState, useEffect } from 'react'
import {
  Dropdown,
  Modal,
  Form,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons'
import { useSelector, useDispatch } from 'react-redux'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import { determineGrammaticalGender } from 'helpers/determineGrammaticalGender'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import RichTextInput from 'components/ui/forms/field/rich-text-input/RichTextInput'
import { EmailType } from 'enum/email-type.enum'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/monitoring.actions'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import { SelectedCollaboratorsSendEmailAPI } from 'api/administration/monitoring/SendEmail'
import styles from './sendEmails.module.scss'

interface SendEmailsProps {
  filterData?: IFilterData
  searchText?: string
  selected: string[]
  allSelected: boolean
  selectedCount: number
}

const SendEmail: React.FC<SendEmailsProps> = ({
  filterData,
  searchText,
  selected,
  allSelected,
  selectedCount,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const tPredefinedPrefix = 'administration.monitor.sendEmail.form.predefinedV2'
  const [showModal, setShowModal] = useState(false)
  const [selectedType, setSelectedType] = useState(EmailType.Invitation)
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')

  const { course } = useSelector(({ courses }: IMainState) => courses)
  const { user } = useSelector(({ user }: IMainState) => user)

  const handleClose = () => {
    setShowModal(false)
    setSelectedType(EmailType.Invitation)
  }

  const handleSend = () => {
    dispatch({
      type: fromActions.SEND_EMAIL,
      payload: {
        selectedCollaborators: {
          all: allSelected,
          add: allSelected ? [] : selected,
          discard: allSelected ? selected : [],
        } as SelectedCollaboratorsSendEmailAPI,
        courseId: course.id,
        subject,
        content,
        filterData,
        searchText,
      },
    })
    setShowModal(false)
  }

  const emailTypes = [
    {
      label: t(`${tPredefinedPrefix}.${EmailType.Invitation}.title`),
      value: EmailType.Invitation,
    },
    {
      label: t(`${tPredefinedPrefix}.${EmailType.Reminder}.title`),
      value: EmailType.Reminder,
    },
    {
      label: t(`${tPredefinedPrefix}.${EmailType.Personalized}.title`),
      value: EmailType.Personalized,
    },
  ]

  const disableContent = [EmailType.Invitation].includes(selectedType)

  const joditConfig = {
    buttons: 'bold,italic,underline,link',
    disabled: disableContent,
  }

  useEffect(() => {
    const app = t('commons.navBar.title')
    const context = determineGrammaticalGender(app)
    const options = { app, context }
    setSubject(t(`${tPredefinedPrefix}.${selectedType}.subject`, options))
    setContent(
      t(`${tPredefinedPrefix}.${selectedType}.content`, {
        courseName: course.name,
        ...options,
      })
    )
  }, [course.name, selectedType, t])

  return (
    <>
      <Dropdown.Item
        disabled={selectedCount < 1}
        className={styles.buttonTitle}
        onClick={() => setShowModal(true)}
      >
        {t('administration.courses.list.dropdown.sendEmail')}
      </Dropdown.Item>

      <Modal
        size="lg"
        show={showModal}
        onHide={handleClose}
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton className="ml-3 mr-3 mt-2 pb-2">
          <Modal.Title>
            <p className={styles.headFont}>
              {t('administration.monitor.sendEmail.title')}
            </p>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ml-3 mr-3">
          <Form>
            <Form.Group>
              <div className={styles.typeLabel}>
                {t('administration.monitor.sendEmail.form.type')}:
              </div>
              <ToggleButtonGroup
                type="radio"
                name="email-type"
                defaultValue={EmailType.Invitation}
                className={styles.emailTypes}
              >
                {emailTypes.map((type, index) => (
                  <ToggleButton
                    key={index}
                    value={type.value}
                    onChange={() => setSelectedType(type.value)}
                  >
                    {type.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Form.Group>
            <Form.Group>
              <InputLabel
                className={styles.typeLabel}
                label={t('administration.monitor.sendEmail.form.subject')}
              />
              <TextInput
                name="subject"
                value={subject}
                onChange={(e: any) => setSubject(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <InputLabel
                className={styles.typeLabel}
                label={t('administration.monitor.sendEmail.form.content')}
              />
              <RichTextInput
                name="content"
                key={selectedType}
                value={content}
                onChange={setContent}
                config={joditConfig}
              />
              <div
                className={styles.countLabel}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    t('administration.monitor.sendEmail.form.warning_message', {
                      count: selectedCount,
                      course: course.name,
                    })
                  ),
                }}
              ></div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="ml-3 mr-3 mt-2">
          <Button
            variant={ButtonVariant.Secondary}
            size={ButtonSizes.Small}
            onClick={handleClose}
          >
            {t('commons.buttons.cancel')}
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            size={ButtonSizes.Small}
            onClick={handleSend}
            disabled={!subject.length || !content.length || !selectedCount}
          >
            <FontAwesomeIcon className="mr-2" icon={faPaperPlane} />
            {t('commons.buttons.send')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default SendEmail
